import { useState, FC, useRef, RefObject, useEffect } from 'react';
import {
    Button, Dialog, DialogContent, DialogTitle, DialogActions,
    Paper, IconButton, Box, Alert, AlertTitle
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { PaperProps } from "@mui/material/Paper";
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { normalizeString } from 'utils/Validations'
import { TimekeeperService } from 'services/TimekeeperService';
import { AccrualRule, AccrualRuleUpdate, AccrualRuleRGrantUpdate, AccrualRuleRLimitUpdate, AccrualRuleRUseBalanceUpdate } from 'repository/timekeeper/@types/AccrualRule';
import { AccrualRuleForm, AccrualRuleFormData } from './form';
import { accrualRuleSchema } from './accrualFormSchema';
import { useAccrualRuleData } from './hooks/useAccrualRuleData';

const timekeeperService = new TimekeeperService();

interface UpdateProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: AccrualRule[];
    rowToEdit: AccrualRule;
    reloading: () => void;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const normalizeTimeToHHmm = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined) return '';

    const raw = String(value).trim();
    if (!raw) return '';

    // Accepts HH:mm and HH:mm:ss and normalizes to HH:mm.
    const directMatch = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (directMatch) {
        const hours = directMatch[1].padStart(2, '0');
        const minutes = directMatch[2];
        return `${hours}:${minutes}`;
    }

    // Fallback for ISO-like values (e.g. 2026-04-28T08:30:00).
    const isoMatch = raw.match(/T(\d{2}):(\d{2})/);
    if (isoMatch) {
        return `${isoMatch[1]}:${isoMatch[2]}`;
    }

    return '';
};

export const UpdateAccrualRule: FC<UpdateProps> = ({ open, setOpen, rows, rowToEdit, reloading }) => {

    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar();
    const { availableData, loadingStates, loadData } = useAccrualRuleData();


    const form = useForm<AccrualRuleFormData>({
        resolver: zodResolver(accrualRuleSchema(t, rows, rowToEdit?.id)),
        defaultValues: {
            name: '', description: '', adjustBalance: false,
            accrualCodeId: '' as number | '', accrualSurplusId: null, referenceDateServiceId: null,
        },
    });

    // Estado de las relaciones (grants, limits, use_balances)
    const [relationsData, setRelationsData] = useState({
        grants: [] as AccrualRuleRGrantUpdate[],
        limits: [] as AccrualRuleRLimitUpdate[],
        useBalances: [] as AccrualRuleRUseBalanceUpdate[],
    });
    const [loading, setLoading] = useState(false)
    const [alerts, setAlerts] = useState(false)

    useEffect(() => {
        if (!open || !rowToEdit) return;

        form.reset({
            name: rowToEdit.name ?? '',
            description: rowToEdit.description ?? '',
            adjustBalance: rowToEdit.adjust_balance ?? false,
            accrualCodeId: rowToEdit.accrual_code_id ?? '',
            accrualSurplusId: rowToEdit.accrual_surplus_id ?? null,
            referenceDateServiceId: rowToEdit.reference_date_service_id ?? null,
        });

        // Si no hay grants/limits, inicializar con una fila vacía
        const mappedLimits = (rowToEdit.limits && rowToEdit.limits.length > 0)
            ? rowToEdit.limits.map((limit) => {
                if (limit.limit_amount_unit === 'HORAS') {
                    return {
                        ...limit,
                        limit_amount: normalizeTimeToHHmm(limit.limit_amount),
                    };
                }

                return limit;
            })
            : [{ limit_id: 0, limit_amount: 0, limit_amount_unit: '' }];

        setRelationsData({
            grants: (rowToEdit.grants && rowToEdit.grants.length > 0)
                ? rowToEdit.grants
                : [{ grant_id: 0, grant_amount: 0 }],
            limits: mappedLimits,
            useBalances: rowToEdit.use_balances ?? [],
        });

        loadData();

    }, [open, rowToEdit, form, loadData]);

    const setUseBalances = (useBalances: AccrualRuleRUseBalanceUpdate[]) => {
        setRelationsData((prev) => ({ ...prev, useBalances }));
    };

    const existName = (name: string) => {
        const normalized = normalizeString(name);
        if (normalizeString(rowToEdit?.name ?? '') === normalized) return false;
        return rows.some((row) => normalizeString(row.name) === normalized);
    };

    const changes = () => {
        if (form.watch('name')) setAlerts(true)
        else setOpen(false);
    }

    const closeForm = () => {
        setOpen(false);
        setAlerts(false);
        setLoading(false);
    }

    const Update = form.handleSubmit(async (formData) => {
        setLoading(true);
        console.log('Form Data to update:', formData);

        // Filtrar grants eliminando grant_id = 0 y accrual_rule_id
        const filteredGrants = relationsData.grants
            .filter(g => g.grant_id && g.grant_id > 0)
            .map(({ grant_id, grant_amount }) => ({ grant_id, grant_amount }));

        // Filtrar limits eliminando limit_id = 0 y accrual_rule_id
        const filteredLimits = relationsData.limits
            .filter(l => l.limit_id && l.limit_id > 0)
            .map(({ limit_id, limit_amount, limit_amount_unit }) => ({ limit_id, limit_amount, limit_amount_unit }));

        // Filtrar use_balances eliminando paycode_id = 0
        const filteredUseBalances = relationsData.useBalances
            .filter(ub => ub.paycode_id && ub.paycode_id > 0)
            .map(({ paycode_id }) => ({ paycode_id }));

        const dataToUpdate: AccrualRuleUpdate = {
            name: formData.name,
            description: formData.description || null,
            adjust_balance: formData.adjustBalance,
            accrual_code_id: formData.accrualCodeId as number,
            accrual_surplus_id: formData.accrualSurplusId,
            reference_date_service_id: formData.referenceDateServiceId,
            grants: filteredGrants.length > 0 ? filteredGrants : [],
            limits: filteredLimits.length > 0 ? filteredLimits : [],
            use_balances: filteredUseBalances.length > 0 ? filteredUseBalances : []
        }

        try {
            console.log('Accrual Rule to update:', dataToUpdate);
            const updatedRule = await timekeeperService.accrual_rule.update(rowToEdit.accrual_rule_id, dataToUpdate);
            console.log('Updated Accrual Rule:', updatedRule);

            enqueueSnackbar(t('accruals.accrual_rules.dialog.alerts.update.success'), { variant: 'success' });
            closeForm();
            reloading();

        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('accruals.accrual_rules.dialog.alerts.update.error'), { variant: 'error' });
            setLoading(false);
        }
    });

    const nameValue = form.watch('name');
    const accrualCodeIdValue = form.watch('accrualCodeId');
    const isSubmitDisabled = loading || !nameValue || !accrualCodeIdValue || accrualCodeIdValue === 0 || existName(nameValue);

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes()
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="md"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.accrual_rules.dialog.titles.edit')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <Alert
                        variant='filled'
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={() => closeForm()} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>
            <DialogContent dividers>
                <AccrualRuleForm
                    form={form}
                    relationsData={relationsData}
                    setRelationsData={setRelationsData}
                    availableData={availableData}
                    loadingStates={loadingStates}
                    existName={existName}
                    setUseBalances={setUseBalances}
                    accrualRuleId={rowToEdit?.accrual_rule_id}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => changes()} variant='outlined' disabled={loading}>
                    {t('general.cancel')}
                </Button>
                <Button
                    onClick={Update}
                    variant='contained'
                    disabled={isSubmitDisabled}
                >
                    {t('general.update')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
